// AdminSidebar.jsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";

import {
  LayoutDashboard,
  Link2,
  FileText,
  ClipboardList,
  Megaphone,
  Trophy,
  Download,
  Award,
  GraduationCap,
  BookOpen,
  Building2,
  UserPlus,
  BadgeInfo,
  BookMarked,
  UserCheck,
  ShieldCheck,
  FileEdit,
  Users2,
  Image as ImageIcon,
  GalleryHorizontal,
  Search,
  Settings,
  Activity,
  KeyRound,
  LogOut,
  ChevronLeft,
  ChevronDown,
  Stethoscope,
  List,
  ListTree,
  ListOrdered,
  MoreHorizontal,
  Briefcase,
  UserSquare2,
  Tags,
  Images,
  CalendarDays,   // ← ADDED for Events section
} from "lucide-react";


// ============================================================
// SIDEBAR SECTIONS
// ============================================================

const SECTIONS = [
  {
    label: "Overview",

    items: [
      {
        label: "Dashboard",
        path: "dashboard",
        icon: LayoutDashboard,
      },
    ],
  },

  // ==========================================================
  // CONTENT
  // ==========================================================

  {
    label: "Content Management",
    icon: FileEdit,

    items: [
       {
            label: "Main-menu",
            path: "contents/main-menu",
            icon: List,
          },

          {
            label: "Sub-menu",
            path: "contents/sub-menu",
            icon: ListTree,
          },

          {
            label: "Sub-sub menu",
            path: "contents/sub-sub-menu",
            icon: ListOrdered,
          },

          {
            label: "Other Contents",
            path: "contents/other",
            icon: MoreHorizontal,
          },
     


    
    ],
  },


   // ==========================================================
  // Gallery and Banners
  // ==========================================================

  {
    label: "Gallery & Banners",
    icon: Images,

    items: [
     {
            label: "Gallery Category",
            path: "gallery/category",
            icon: Tags,
          },

          {
            label: "Photo Gallery",
            path: "gallery/photos",
            icon: Images,
          },

      {
        label: "Manage Banners",
        path: "banners",
        icon: ImageIcon,
      },
 
    

     

    
    ],
  },

     // ==========================================================
  // Faculty Management
  // ==========================================================

  {
    label: "Faculty Management",
    icon: Users2,

    items: [
    {
            label: "Department",
            path: "faculty/department",
            icon: Building2,
          },

          {
            label: "Designation",
            path: "faculty/designation",
            icon: Briefcase,
          },

          {
            label: "Faculty Details",
            path: "faculty/details",
            icon: UserSquare2,
          },
 
    

     

    
    ],
  },


  // ==========================================================
  // Tender and Notice
  // ==========================================================

  {
    label: "Tender & Notice",
    icon: FileText,

    items: [
       {
        label: "Tender Management",
        path: "tenders",
        icon: FileText,
      },

          {
        label: "Manage Notice Board",
        path: "notice-board",
        icon: ClipboardList,
      },

         
      {
        label: "Manage Advertisement",
        path: "advertisement",
        icon: Megaphone,
      },

      {
        label: "Manage Success",
        path: "success",
        icon: Trophy,
      },

      
  {
        label: "Link to Other Websites",
        path: "links",
        icon: Link2,
      },
 
    

     

    
    ],
  },
  // ==========================================================
  // ACADEMICS & ADMISSIONS
  // ==========================================================

  {
    label: "Academics & Admissions",
    icon: GraduationCap,

    items: [
      {
        label: "Course",
        path: "course",
        icon: BookOpen,
      },

      {
        label: "Department",
        path: "department",
        icon: Building2,
      },

      {
        label: "Admission",
        path: "admission",
        icon: GraduationCap,
      },

      {
        label: "Manage Admission",
        path: "manage-admission",
        icon: UserCheck,
      },

      {
        label: "Student Registration Form",
        path: "student-registration",
        icon: UserPlus,
      },

      {
        label: "MCI Information",
        path: "mci-information",
        icon: BadgeInfo,
      },

      {
        label: "Manage Govt. Approval",
        path: "govt-approval",
        icon: ShieldCheck,
      },

      {
        label: "Results",
        path: "results",
        icon: Award,
      },

      {
        label: "Download Forms",
        path: "download-forms",
        icon: Download,
      },       
      {
        label: "Manage Events",
        path: "events",
        icon: CalendarDays,
      },   
    ],
  },


  // ==========================================================
  // Others
  // ==========================================================

  {
    label: "Others",
    icon: Settings,

    items: [
   {
        label: "Manage Setting",
        path: "settings",
        icon: Settings,
      },

      {
        label: "Activity Report",
        path: "activity-report",
        icon: Activity,
      },

      {
        label: "Manage SEO",
        path: "seo",
        icon: Search,
      },

      
    ],
  },


  // ==========================================================
  // CHANGE PASSWORD
  // ==========================================================

  {
    label: "Change Password",

    items: [
      {
        label: "Change Password",
        path: "change-password",
        icon: KeyRound,
      },
    ],
  },
];


// ============================================================
// CHECK CHILD ACTIVE
// ============================================================

const isChildActive = (item, pathname) =>
  !!item.children?.some(
    (c) => pathname === `/admin/${c.path}`
  );


// ============================================================
// CHECK SECTION ACTIVE
// ============================================================

const sectionHasActivePath = (section, pathname) =>
  section.items.some((item) => {
    if (item.path && `/admin/${item.path}` === pathname) {
      return true;
    }

    return isChildActive(item, pathname);
  });


// ============================================================
// MAIN COMPONENT
// ============================================================

export default function AdminSidebar({ onLogout }) {

  const [collapsed, setCollapsed] = useState(false);

  const location = useLocation();

  const [openSection, setOpenSection] = useState(null);

  const [openMenu, setOpenMenu] = useState(null);


  // ==========================================================
  // AUTO OPEN CURRENT SECTION
  // ==========================================================

  useEffect(() => {

    for (const section of SECTIONS) {

      if (section.label === "Overview") {
        continue;
      }

      if (
        sectionHasActivePath(
          section,
          location.pathname
        )
      ) {

        setOpenSection(section.label);

        const match = section.items.find(
          (item) =>
            isChildActive(
              item,
              location.pathname
            )
        );

        if (match) {
          setOpenMenu(match.label);
        }

        break;
      }
    }

  }, []);


  // ==========================================================
  // TOGGLE SECTION
  // ==========================================================

  const toggleSection = (label) => {

    setOpenSection((prev) =>
      prev === label ? null : label
    );

    setOpenMenu(null);
  };


  // ==========================================================
  // TOGGLE SUB MENU
  // ==========================================================

  const toggleMenu = (label) => {

    setOpenMenu((prev) =>
      prev === label ? null : label
    );
  };


  // ==========================================================
  // RETURN
  // ==========================================================

  return (

    <motion.aside

      initial={{
        x: -24,
        opacity: 0,
      }}

      animate={{
        x: 0,
        opacity: 1,
      }}

      transition={{
        type: "spring",
        stiffness: 220,
        damping: 26,
      }}

      className={`
        mkcg-sidebar
        h-screen
        sticky
        top-0
        shrink-0
        flex
        flex-col
        transition-[width]
        duration-300
        ease-out
        ${
          collapsed
            ? "w-[78px]"
            : "w-[272px]"
        }
      `}
    >


      {/* =====================================================
          BRAND
      ===================================================== */}

      <div
        className="
          h-16
          flex
          items-center
          gap-3
          px-4
          border-b
          border-slate-100
          shrink-0
        "
      >
{/* 
        <div
          className="
            w-10
            h-10
            rounded-xl
            flex
            items-center
            justify-center
            shrink-0
            shadow-sm
          "

          style={{
            background:
              "linear-gradient(135deg,#0f766e,#14b8a6)",
          }}
        >

          <Stethoscope
            size={18}
            className="text-white"
          />

        </div> */}

<Link
  to="/admin/dashboard"
  style={{
    display: "flex",
    alignItems: "center",
    gap: "8px",
    textDecoration: "none",
  }}
>
  <img
    src="/favicon.svg"
    alt="MKCG Medical College & Hospital logo"
    className=""
    style={{ width: "25px" }}
  />

  <AnimatePresence initial={false}>
    {!collapsed && (
      <motion.div
        key="brand"
        initial={{ opacity: 0, x: -6 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -6 }}
        transition={{ duration: 0.18 }}
        className="leading-tight overflow-hidden"
      >
        <p style={{ color: "#fff", textAlign: "center" }}>
          MKCG Admin
        </p>

        <p className="text-[11px] text-slate-400 whitespace-nowrap">
          Brahmapur, Odisha
        </p>
      </motion.div>
    )}
  </AnimatePresence>
</Link>


        {/* =================================================
            COLLAPSE BUTTON
        ================================================= */}

        <button

          type="button"

          onClick={() =>
            setCollapsed((v) => !v)
          }

          className="
            ml-auto
            w-7
            h-7
            rounded-md
            border
            border-slate-200
            text-slate-400
            hover:text-slate-700
            hover:border-slate-300
            flex
            items-center
            justify-center
            transition
            shrink-0
          "

          aria-label="Toggle sidebar"
        >

          <ChevronLeft
            size={14}
            className={`
              transition-transform
              ${
                collapsed
                  ? "rotate-180"
                  : ""
              }
            `}
          />

        </button>

      </div>


      {/* =====================================================
          NAVIGATION
      ===================================================== */}

      <nav
        className="
          flex-1
          overflow-y-auto
          space-y-3
        "
      >

        {SECTIONS.map((section) => {


          // =================================================
          // LEAF SECTION
          // Dashboard / Change Password
          // =================================================

          const isLeafSection =
            section.items.length === 1 &&
            !section.items[0].children;


          if (isLeafSection) {

            return (

              <div
                key={section.label}
                className="space-y-1"
              >

                {section.items.map((m) => {

                  const Icon = m.icon;

                  const fullPath =
                    `/admin/${m.path}`;

                  const isActive =
                    location.pathname === fullPath;


                  return (

                    <motion.div

                      key={m.label}

                      whileHover={{
                        x: collapsed ? 0 : 2,
                      }}

                      whileTap={{
                        scale: 0.98,
                      }}
                      
                    >

                      <Link

                        to={fullPath}

                        title={
                          collapsed
                            ? m.label
                            : undefined
                        }

                        className={`
                          nav-item
                          ${
                            isActive
                              ? "is-active"
                              : ""
                          }
                        `}
                      >

                        {isActive && (

                          <motion.span

                            layoutId="activePill"

                            transition={{
                              type: "spring",
                              stiffness: 380,
                              damping: 30,
                            }}

                            className="
                              absolute
                              left-0
                              top-1/2
                              -translate-y-1/2
                              h-6
                              w-[3px]
                              rounded-r-full
                            "
                            style={{ backgroundColor:"#359149ba" }}
                          />

                        )}


                        <Icon
                          size={18}
                          className="
                            nav-icon
                            shrink-0
                          "
                        />


                        {!collapsed && (

                          <span className="truncate">
                            {m.label}
                          </span>

                        )}

                      </Link>

                    </motion.div>

                  );
                })}

              </div>

            );
          }


          // =================================================
          // COLLAPSIBLE SECTION
          // Content / Academics / Administration
          // =================================================

          const sectionOpen =
            openSection === section.label;

          const sectionActive =
            sectionHasActivePath(
              section,
              location.pathname
            );


          // IMPORTANT:
          // Section ka icon yahan se milega
          const SectionIcon = section.icon;


          return (

            <div key={section.label}>


              {/* ===========================================
                  SECTION HEADER
              =========================================== */}

              <motion.button

                type="button"

                onClick={() =>
                  toggleSection(section.label)
                }

                whileHover={{
                  x: collapsed ? 0 : 2,
                }}

                whileTap={{
                  scale: 0.98,
                }}

                title={
                  collapsed
                    ? section.label
                    : undefined
                }

                className={`
                  nav-item
                  w-full
                  ${
                    sectionActive
                      ? "is-active"
                      : ""
                  }
                `}
                style={{ backgroundColor:"#359149ba" }}
              >


                {/* Active indicator */}

                {sectionActive && (

                  <motion.span

                    layoutId="activePill"

                    transition={{
                      type: "spring",
                      stiffness: 380,
                      damping: 30,
                    }}

                    className="
                      absolute
                      left-0
                      top-1/2
                      -translate-y-1/2
                      h-6
                      w-[3px]
                      rounded-r-full
                    "
                    style={{ backgroundColor:"#359149ba" }}
                  />

                )}


                {/* ========================================
                    SECTION ICON
                ======================================== */}

                {SectionIcon && (

                  <SectionIcon

                    size={18}

                    strokeWidth={1.8}

                    className="
                      nav-icon
                      shrink-0
                    "
                  />

                )}


                {/* ========================================
                    SECTION NAME
                ======================================== */}

                {!collapsed && (

                  <span
                    className="
                      truncate
                      flex-1
                      text-left
                      font-semibold
                     
                    "
                  >
                    {section.label}
                  </span>

                )}


                {/* ========================================
                    ARROW
                ======================================== */}

                {!collapsed && (

                  <ChevronDown

                    size={14}

                    className={`
                      shrink-0
                      text-slate-400
                      transition-transform
                      ${
                        sectionOpen
                          ? "rotate-180"
                          : ""
                      }
                    `}
                  />

                )}

              </motion.button>


              {/* =================================================
                  SECTION CHILDREN
              ================================================= */}

              <AnimatePresence initial={false}>

                {sectionOpen && !collapsed && (

                  <motion.div

                    initial={{
                      height: 0,
                      opacity: 0,
                    }}

                    animate={{
                      height: "auto",
                      opacity: 1,
                    }}

                    exit={{
                      height: 0,
                      opacity: 0,
                    }}

                    transition={{
                      duration: 0.18,
                    }}

                    className="overflow-hidden"
                    style={{ backgroundColor:"#359149ba" }}
                  >

                    <div
                      className="
                        sub1
                        space-y-1
                        mt-1
                      "
                       style={{ backgroundColor:"#35916f" }}
                    >

                      {section.items.map((m) => {

                        const Icon = m.icon;


                        // =================================================
                        // PARENT ITEM WITH CHILDREN
                        // =================================================

                        if (m.children) {

                          const open =
                            openMenu === m.label;

                          const childActive =
                            isChildActive(
                              m,
                              location.pathname
                            );


                          return (

                            <div
                              key={m.label}
                            >

                              <motion.button

                                type="button"

                                onClick={() =>
                                  toggleMenu(m.label)
                                }

                                whileHover={{
                                  x: 2,
                                }}

                                whileTap={{
                                  scale: 0.98,
                                }}

                                className={`
                                  nav-item
                                  w-full
                                  ${
                                    childActive
                                      ? "is-active"
                                      : ""
                                  }
                                `}
                                style={{ backgroundColor:"#359149ba" }}
                              >

                                {childActive && (

                                  <motion.span

                                    layoutId="activeSubPill"

                                    transition={{
                                      type: "spring",
                                      stiffness: 380,
                                      damping: 30,
                                    }}

                                    className="
                                      absolute
                                      left-0
                                      top-1/2
                                      -translate-y-1/2
                                      h-6
                                      w-[3px]
                                      rounded-r-full
                                    "
                                    style={{ backgroundColor:"#359149ba" }}
                                  />

                                )}


                                <Icon
                                  size={18}
                                  className="
                                    nav-icon
                                    shrink-0
                                  "
                                />


                                <span
                                  className="
                                    truncate
                                    flex-1
                                    text-left
                                  "
                                >
                                  {m.label}
                                </span>


                                <ChevronDown

                                  size={14}

                                  className={`
                                    shrink-0
                                    text-slate-400
                                    transition-transform
                                    ${
                                      open
                                        ? "rotate-180"
                                        : ""
                                    }
                                  `}
                                />

                              </motion.button>


                              {/* =========================================
                                  NESTED CHILDREN
                              ========================================= */}

                              <AnimatePresence initial={false}>

                                {open && (

                                  <motion.div

                                    initial={{
                                      height: 0,
                                      opacity: 0,
                                    }}

                                    animate={{
                                      height: "auto",
                                      opacity: 1,
                                    }}

                                    exit={{
                                      height: 0,
                                      opacity: 0,
                                    }}

                                    transition={{
                                      duration: 0.18,
                                    }}

                                    className="overflow-hidden"
                                     style={{ backgroundColor:"#359149ba" }}
                                  >

                                    <div
                                      className="
                                        pl-3
                                        mt-1
                                        sub
                                        space-y-1
                                        border-l
                                        border-slate-100
                                        ml-2
                                      "
                                    >

                                      {m.children.map(
                                        (c) => {

                                          const ChildIcon =
                                            c.icon;

                                          const fullPath =
                                            `/admin/${c.path}`;

                                          const isActive =
                                            location.pathname ===
                                            fullPath;


                                          return (

                                            <motion.div

                                              key={c.label}

                                              whileHover={{
                                                x: 2,
                                              }}

                                              whileTap={{
                                                scale: 0.98,
                                              }}
                                            >

                                              <Link

                                                to={fullPath}

                                                className={`
                                                  nav-item
                                                  text-[13px]
                                                  ${
                                                    isActive
                                                      ? "is-active"
                                                      : ""
                                                  }
                                                `}
                                              >

                                                {isActive && (

                                                  <motion.span

                                                    layoutId="activeSubPill2"

                                                    transition={{
                                                      type: "spring",
                                                      stiffness: 380,
                                                      damping: 30,
                                                    }}

                                                    className="
                                                      absolute
                                                      left-0
                                                      top-1/2
                                                      -translate-y-1/2
                                                      h-5
                                                      w-[3px]
                                                      rounded-r-full
                                                    "
                                                  />

                                                )}


                                                <ChildIcon

                                                  size={15}

                                                  className="
                                                    nav-icon
                                                    shrink-0
                                                  "
                                                />


                                                <span className="truncate">
                                                  {c.label}
                                                </span>

                                              </Link>

                                            </motion.div>

                                          );

                                        }
                                      )}

                                    </div>

                                  </motion.div>

                                )}

                              </AnimatePresence>

                            </div>

                          );

                        }


                        // =================================================
                        // NORMAL LEAF ITEM
                        // =================================================

                        const fullPath =
                          `/admin/${m.path}`;

                        const isActive =
                          location.pathname ===
                          fullPath;


                        return (

                          <motion.div

                            key={m.label}

                            whileHover={{
                              x: 2,
                            }}

                            whileTap={{
                              scale: 0.98,
                            }}
                               style={{ backgroundColor:"#35916f" }}
                          >

                            <Link

                              to={fullPath}

                              className={`
                                nav-item
                                ${
                                  isActive
                                    ? "is-active"
                                    : ""
                                }
                              `}
                            
                            >

                              {isActive && (

                                <motion.span

                                  layoutId="activePill"

                                  transition={{
                                    type: "spring",
                                    stiffness: 380,
                                    damping: 30,
                                  }}

                                  className="
                                    absolute
                                    left-0
                                    top-1/2
                                    -translate-y-1/2
                                    h-6
                                    w-[3px]
                                    rounded-r-full
                                  "
                                  
                                />

                              )}


                              <Icon

                                size={18}

                                className="
                                  nav-icon
                                  shrink-0
                                "
                              />


                              <span className="truncate text-[14px]">
                                {m.label}
                              </span>

                            </Link>

                          </motion.div>

                        );

                      })}

                    </div>

                  </motion.div>

                )}

              </AnimatePresence>

            </div>

          );

        })}

      </nav>


      {/* =====================================================
          FOOTER / LOGOUT
      ===================================================== */}

      <div
        className="
          border-t
          border-slate-100
          p-3
          shrink-0
        "
      >

        <motion.button

          whileHover={{
            x: 2,
          }}

          whileTap={{
            scale: 0.98,
          }}

          onClick={onLogout}

          title={
            collapsed
              ? "Logout"
              : undefined
          }

          className="
            w-full
            flex
            items-center
            gap-3
            rounded-lg
            px-3
            py-2.5
            text-sm
            font-medium
            text-rose-600
            hover:bg-rose-50
            transition
          "
        >

          <LogOut
            size={18}
            className="shrink-0"
          />

          {!collapsed && (
            <span>
              Logout
            </span>
          )}

        </motion.button>

      </div>

    </motion.aside>
  );
}


// ============================================================
// EXPORT
// ============================================================

export {
  SECTIONS as ADMIN_NAV_SECTIONS
};


// ============================================================
// FLAT NAVIGATION
// ============================================================

export const ADMIN_NAV_FLAT =
  SECTIONS.flatMap((s) =>
    s.items.flatMap((item) =>
      item.children
        ? item.children
        : [item]
    )
  );